import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DataTable, DataTableFilterEvent, DataTablePageEvent, DataTableSelectionSingleChangeEvent, DataTableExpandedRows } from 'primereact/datatable';
import { Column, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Menubar } from 'primereact/menubar';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from 'primereact/api';
import { accountsApi, depositsApi, dataSourcesApi } from '../../api';
import { Account, CustomerDeposit, DataSource } from '../../types';
import { formatCurrency, formatDate } from '../../utils';
import { useDataTableState, useAccountMenuItem } from '../../hooks';

interface EditedCustomerDeposit extends CustomerDeposit {
  edited?: boolean;
}

interface CustomerDepositsTabProps {
  clientId: string;
  onRowSelect?: (
    deposit: CustomerDeposit | null,
    dataSource: DataSource | null,
    pageNumber?: number
  ) => void;
}

export const CustomerDepositsTab: React.FC<CustomerDepositsTabProps> = ({ clientId, onRowSelect }) => {
  const toast = useRef<Toast>(null);
  const tableRef = useRef<DataTable<any>>(null);
  const [deposits, setDeposits] = useState<EditedCustomerDeposit[]>([]);
  const [originalDeposits, setOriginalDeposits] = useState<EditedCustomerDeposit[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  const [selectedDeposit, setSelectedDeposit] = useState<EditedCustomerDeposit | null>(null);
  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});

  // Persistent filter and sort state
  const {
    sortField,
    sortOrder,
    handleSort,
    filters,
    handleFilter: onFilterChange,
    customFilters,
    setCustomFilter,
  } = useDataTableState({
    storageKey: 'dataReview:customerDeposits',
    defaultSort: { field: 'deposit_date', order: -1 },
    defaultFilters: {
      customer_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
    defaultCustomFilters: {
      accountFilter: 'all',
      fileFilter: 'all',
      includePending: true,
      includeApproved: false,
      includeRejected: false,
    },
  });

  // Extract custom filters for easier access
  const accountFilter = customFilters.accountFilter as string;
  const fileFilter = customFilters.fileFilter as string;
  const includePending = customFilters.includePending as boolean;
  const includeApproved = customFilters.includeApproved as boolean;
  const includeRejected = customFilters.includeRejected as boolean;

  // Duplicate review mode is temporary (not persisted)
  const [includeDuplicates, setIncludeDuplicates] = useState(false);

  const getDepositStatus = (d: CustomerDeposit): string => {
    return d.status || 'pending';
  };

  const isStatusApproved = (status: string) => status === 'approved' || status === 'accepted';
  const isStatusRejected = (status: string) => status === 'rejected';
  const isStatusPending = (status: string) => !status || status === 'pending';

  const isRowLocked = (rowData: EditedCustomerDeposit) => {
    const status = getDepositStatus(rowData);
    return isStatusApproved(status);
  };

  // Helper to get display value: final field if set, otherwise raw field
  const getDisplayValue = (row: CustomerDeposit, field: string): any => {
    const finalValue = row[field as keyof CustomerDeposit];
    if (finalValue !== null && finalValue !== undefined && finalValue !== '') {
      return finalValue;
    }
    // Map final fields to raw fields
    const rawFieldMap: Record<string, keyof CustomerDeposit> = {
      deposit_date: 'raw_deposit_date',
      customer_name: 'raw_customer_name',
      amount: 'raw_deposit_amount',
    };
    const rawField = rawFieldMap[field];
    if (rawField) {
      return row[rawField];
    }
    return finalValue;
  };

  // Get data sources that have deposits
  const depositDataSources = useMemo(() => {
    const depositSourceIds = new Set(deposits.map((d) => d.data_source_id));
    return dataSources.filter((ds) => depositSourceIds.has(ds.id));
  }, [dataSources, deposits]);

  // Account filter options - only accounts that have deposit files
  const filteredAccounts = useMemo(() => {
    const accountIds = new Set(
      depositDataSources
        .map((ds) => ds.account_id)
        .filter((id): id is string => id !== null)
    );
    return accounts.filter((a) => accountIds.has(a.id));
  }, [accounts, depositDataSources]);

  const { accountMenuItem } = useAccountMenuItem(clientId, {
    showAllOption: true,
    customAccounts: filteredAccounts,
    customSelectedId: accountFilter === 'all' ? null : accountFilter,
    customSetAccount: (id) => setCustomFilter('accountFilter', id ?? 'all'),
  });

  // File filter options - filtered by selected account
  const fileFilterOptions = useMemo(() => {
    let relevantSources = depositDataSources;

    // Filter by account if one is selected
    if (accountFilter !== 'all') {
      relevantSources = relevantSources.filter((ds) => ds.account_id === accountFilter);
    }

    return [
      { label: 'All files', value: 'all' },
      ...relevantSources.map((ds) => ({
        label: ds.original_filename || ds.name || `Source ${ds.id}`,
        value: ds.id,
      })),
    ];
  }, [depositDataSources, accountFilter]);

  // Count all duplicates in the dataset (for showing the review button)
  const totalDuplicateCount = useMemo(() => {
    return deposits.filter((d) => d.is_potential_duplicate).length;
  }, [deposits]);

  // Group duplicates by their original deposit for the review mode
  const duplicateGroups = useMemo(() => {
    if (!includeDuplicates) return { originals: [], duplicatesByOriginalId: new Map() };

    // Find all duplicates and group by their original
    const duplicates = deposits.filter((d) => d.is_potential_duplicate && d.duplicate_of_id);
    const duplicatesByOriginalId = new Map<string, CustomerDeposit[]>();

    for (const dup of duplicates) {
      const originalId = dup.duplicate_of_id!;
      if (!duplicatesByOriginalId.has(originalId)) {
        duplicatesByOriginalId.set(originalId, []);
      }
      duplicatesByOriginalId.get(originalId)!.push(dup);
    }

    // Get the original deposits that have duplicates
    const originalIds = new Set(duplicatesByOriginalId.keys());
    const originals = deposits.filter((d) => originalIds.has(d.id));

    return { originals, duplicatesByOriginalId };
  }, [deposits, includeDuplicates]);

  const filteredDeposits = useMemo(() => {
    // In duplicate review mode, show originals that have duplicates
    if (includeDuplicates) {
      return duplicateGroups.originals;
    }

    let result = deposits;

    // Filter by account (via data sources)
    if (accountFilter !== 'all') {
      const accountSourceIds = new Set(
        depositDataSources
          .filter((ds) => ds.account_id === accountFilter)
          .map((ds) => ds.id)
      );
      result = result.filter((d) => accountSourceIds.has(d.data_source_id));
    }

    // Filter by file
    if (fileFilter !== 'all') {
      result = result.filter((d) => d.data_source_id === fileFilter);
    }

    // Filter by status
    result = result.filter((d) => {
      const status = getDepositStatus(d);
      if (isStatusPending(status) && !includePending) return false;
      if (isStatusApproved(status) && !includeApproved) return false;
      if (isStatusRejected(status) && !includeRejected) return false;
      return true;
    });

    // Exclude duplicates in normal mode
    result = result.filter((d) => !d.is_potential_duplicate);

    return result;
  }, [deposits, accountFilter, fileFilter, depositDataSources, includePending, includeApproved, includeRejected, includeDuplicates, duplicateGroups]);

  // Get deletable duplicates (based on file/account filter, regardless of includeDuplicates toggle)
  const deletableDuplicates = useMemo(() => {
    let result = deposits;

    // Filter by account (via data sources)
    if (accountFilter !== 'all') {
      const accountSourceIds = new Set(
        depositDataSources
          .filter((ds) => ds.account_id === accountFilter)
          .map((ds) => ds.id)
      );
      result = result.filter((d) => accountSourceIds.has(d.data_source_id));
    }

    // Filter by file
    if (fileFilter !== 'all') {
      result = result.filter((d) => d.data_source_id === fileFilter);
    }

    return result.filter((d) => d.is_potential_duplicate);
  }, [deposits, accountFilter, fileFilter, depositDataSources]);

  // Calculate total amount (excluding duplicates)
  const totalAmount = useMemo(() => {
    return filteredDeposits
      .filter((d) => !d.is_potential_duplicate)
      .reduce((sum, d) => sum + (d.amount || 0), 0);
  }, [filteredDeposits]);

  useEffect(() => {
    loadData();
  }, [clientId]);

  useEffect(() => {
    setFilteredRowCount(filteredDeposits.length);
  }, [filteredDeposits]);

  // Reset file filter when account filter changes
  useEffect(() => {
    setCustomFilter('fileFilter', 'all');
  }, [accountFilter]);

  const handleFilter = (event: DataTableFilterEvent) => {
    onFilterChange(event);
    const filtered = event.filteredValue as CustomerDeposit[] | null;
    setFilteredRowCount(filtered ? filtered.length : filteredDeposits.length);
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [depositsData, sourcesData, accountsData] = await Promise.all([
        depositsApi.listDeposits(clientId),
        dataSourcesApi.listDataSources(clientId),
        accountsApi.listAccounts(clientId),
      ]);
      const editedDepositsData = depositsData.map(d => ({ ...d, edited: false }));
      setDeposits(editedDepositsData);
      setOriginalDeposits(editedDepositsData.map(d => ({ ...d })));
      setDataSources(sourcesData);
      setAccounts(accountsData);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load deposits',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectionChange = (e: DataTableSelectionSingleChangeEvent<CustomerDeposit[]>) => {
    const selected = e.value as CustomerDeposit | null;
    setSelectedDeposit(selected);
    if (onRowSelect) {
      const dataSource = selected
        ? dataSources.find((ds) => ds.id === selected.data_source_id) || null
        : null;
      onRowSelect(selected, dataSource);
    }
  };

  const handleApprove = (deposit: CustomerDeposit) => {
    confirmDialog({
      message: 'Are you sure you want to approve this deposit?',
      header: 'Confirm Approval',
      icon: 'ms-out ms-out-check-circle icon-success',
      acceptClassName: 'p-button-success',
      accept: async () => {
        try {
          const updated = await depositsApi.approveDeposit(clientId, deposit.id);
          setDeposits(deposits.map((d) => (d.id === deposit.id ? updated : d)));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Deposit approved',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to approve deposit',
            life: 5000,
          });
        }
      },
    });
  };

  const handleReject = (deposit: CustomerDeposit) => {
    confirmDialog({
      message: 'Are you sure you want to reject this deposit?',
      header: 'Confirm Rejection',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const updated = await depositsApi.rejectDeposit(clientId, deposit.id);
          setDeposits(deposits.map((d) => (d.id === deposit.id ? updated : d)));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Deposit rejected',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to reject deposit',
            life: 5000,
          });
        }
      },
    });
  };

  const handleUnlock = (deposit: CustomerDeposit) => {
    confirmDialog({
      message: 'This deposit is approved. Unlock to allow edits?',
      header: 'Confirm Unlock Deposit',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-warning',
      acceptLabel: 'Unlock',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          const updated = await depositsApi.unlockDeposit(clientId, deposit.id);
          setDeposits(deposits.map((d) => (d.id === deposit.id ? updated : d)));
          toast.current?.show({
            severity: 'success',
            summary: 'Unlocked',
            detail: 'Deposit unlocked for editing',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to unlock deposit',
            life: 5000,
          });
        }
      },
    });
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    if (isRowLocked(options.rowData as EditedCustomerDeposit)) {
      return <span>{options.value || ''}</span>;
    }

    const rowData = options.rowData as CustomerDeposit;
    // Use display value (final || raw) as the initial editor value
    const displayValue = getDisplayValue(rowData, options.field);

    if (options.field === 'amount') {
      const numValue = typeof displayValue === 'string' ? parseFloat(displayValue.replace(/[$,]/g, '')) : displayValue;
      return (
        <InputNumber
          value={isNaN(numValue) ? null : numValue}
          onValueChange={(e) => options.editorCallback?.(e.value)}
          mode="currency"
          currency="USD"
          locale="en-US"
          minFractionDigits={2}
          className="full-width-input"
        />
      );
    }

    if (options.field === 'deposit_date') {
      let dateValue = displayValue || '';
      if (dateValue && !dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parsed = new Date(dateValue);
        if (!isNaN(parsed.getTime())) {
          dateValue = parsed.toISOString().split('T')[0];
        }
      }
      return (
        <InputText
          type="date"
          value={dateValue}
          onChange={(e) => options.editorCallback?.(e.target.value)}
          className="full-width-input"
        />
      );
    }

    return (
      <InputText
        type="text"
        value={displayValue || ''}
        onChange={(e) => options.editorCallback?.(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        className="full-width-input"
        autoFocus
      />
    );
  };

  const onCellEditComplete = async (e: ColumnEvent) => {
    const { rowData, newValue, field } = e;
    const deposit = rowData as EditedCustomerDeposit;

    if (isRowLocked(deposit)) return;

    // Compare with display value (final || raw) to see if actually changed
    const currentDisplayValue = getDisplayValue(deposit, field);
    if (newValue === currentDisplayValue) return;

    // Update locally first and mark as edited
    const updatedDeposit: EditedCustomerDeposit = {
      ...deposit,
      [field]: newValue,
      edited: true,
    };

    setDeposits((prev) => prev.map((d) => (d.id === deposit.id ? updatedDeposit : d)));
    setSelectedDeposit((prev) => (prev && prev.id === deposit.id ? updatedDeposit : prev));

    // Save to backend
    try {
      const updateData: Record<string, any> = { [field]: newValue };
      const saved = await depositsApi.updateDeposit(clientId, deposit.id, updateData);
      setDeposits((prev) => prev.map((d) => (d.id === deposit.id ? { ...saved, edited: false } : d)));
      setSelectedDeposit((prev) => (prev && prev.id === deposit.id ? { ...saved, edited: false } : prev));
    } catch (error: any) {
      // Revert on error
      setDeposits((prev) => prev.map((d) => (d.id === deposit.id ? deposit : d)));
      setSelectedDeposit((prev) => (prev && prev.id === deposit.id ? deposit : prev));
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to save changes',
        life: 5000,
      });
    }
  };

  const handleUndo = (depositId: string) => {
    const original = originalDeposits.find((d) => d.id === depositId);
    if (!original) return;

    setDeposits((prev) => prev.map((d) => (d.id === depositId ? { ...original, edited: false } : d)));
    setSelectedDeposit((prev) => (prev && prev.id === depositId ? { ...original, edited: false } : prev));
    toast.current?.show({
      severity: 'info',
      summary: 'Undone',
      detail: 'Changes reverted',
      life: 2000,
    });
  };

  const handleApproveAll = () => {
    const pendingDeposits = filteredDeposits.filter((d) => isStatusPending(getDepositStatus(d)));
    if (pendingDeposits.length === 0) {
      toast.current?.show({
        severity: 'info',
        summary: 'No Pending',
        detail: 'No pending deposits to approve',
        life: 3000,
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to approve all ${pendingDeposits.length} pending deposits?`,
      header: 'Approve All Deposits',
      icon: 'ms-out ms-out-check-circle icon-success',
      acceptClassName: 'p-button-success',
      accept: async () => {
        setIsApprovingAll(true);
        try {
          for (const deposit of pendingDeposits) {
            await depositsApi.approveDeposit(clientId, deposit.id);
          }
          await loadData();
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `${pendingDeposits.length} deposits approved`,
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to approve all deposits',
            life: 5000,
          });
        } finally {
          setIsApprovingAll(false);
        }
      },
    });
  };

  const handleShowDuplicates = useCallback(() => {
    // Set all filters to show duplicates
    setCustomFilter('accountFilter', 'all');
    setCustomFilter('fileFilter', 'all');
    setCustomFilter('includePending', true);
    setIncludeDuplicates(true);

    // Expand all rows by default - need to wait for state to update
    setTimeout(() => {
      const allOriginalIds = deposits
        .filter((d) => d.is_potential_duplicate && d.duplicate_of_id)
        .map((d) => d.duplicate_of_id!)
        .filter((id, index, arr) => arr.indexOf(id) === index);

      const expanded: DataTableExpandedRows = {};
      allOriginalIds.forEach((id) => {
        expanded[id] = true;
      });
      setExpandedRows(expanded);
    }, 0);
  }, [deposits, setCustomFilter]);

  const handleDeleteVisibleDuplicates = () => {
    if (deletableDuplicates.length === 0) return;

    const fileName = fileFilterOptions.find((o) => o.value === fileFilter)?.label || 'this file';

    confirmDialog({
      message: `Are you sure you want to delete ${deletableDuplicates.length} duplicate deposits from "${fileName}"? This action cannot be undone.`,
      header: 'Delete Duplicates',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          const depositIds = deletableDuplicates.map((d) => d.id);
          const result = await depositsApi.bulkDeleteDeposits(clientId, depositIds);
          await loadData();
          toast.current?.show({
            severity: 'success',
            summary: 'Deleted',
            detail: `${result.deleted_count} duplicate deposits deleted`,
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete duplicates',
            life: 5000,
          });
        }
      },
    });
  };

  const currentFileLabel = useMemo(() => {
    if (fileFilter === 'all') return 'All files';
    const option = fileFilterOptions.find((o) => o.value === fileFilter);
    return option?.label || 'All files';
  }, [fileFilter, fileFilterOptions]);

  const menuItems = [
    accountMenuItem,
    {
      label: `${currentFileLabel}`,
      icon: 'ms-out ms-out-description',
      items: fileFilterOptions.map((opt) => ({
        label: opt.label,
        command: () => setCustomFilter('fileFilter', opt.value),
      })),
    },
    {
      label: 'Approve All',
      icon: 'ms-out ms-out-done-all',
      command: handleApproveAll,
      disabled: isApprovingAll || !filteredDeposits.some((d) => isStatusPending(getDepositStatus(d))),
    },
    {
      label: 'Status',
      icon: 'ms-out ms-out-visibility',
      items: [
        {
          label: includePending ? 'Hide Pending' : 'Show Pending',
          command: () => setCustomFilter('includePending', !includePending),
        },
        {
          label: includeApproved ? 'Hide Approved' : 'Show Approved',
          command: () => setCustomFilter('includeApproved', !includeApproved),
        },
        {
          label: includeRejected ? 'Hide Rejected' : 'Show Rejected',
          command: () => setCustomFilter('includeRejected', !includeRejected),
        },
      ],
    },
  ];

  const amountBodyTemplate = (rowData: CustomerDeposit) => {
    // Show final value if set, otherwise raw value
    if (rowData.amount !== null && rowData.amount !== undefined) {
      return formatCurrency(rowData.amount);
    }
    if (rowData.raw_deposit_amount) {
      const numValue = parseFloat(rowData.raw_deposit_amount.replace(/[$,]/g, ''));
      if (!isNaN(numValue)) {
        return <span className="raw-value">{formatCurrency(numValue)}</span>;
      }
      return <span className="raw-value">{rowData.raw_deposit_amount}</span>;
    }
    return '—';
  };

  const customerNameBodyTemplate = (rowData: CustomerDeposit) => {
    // Show final value if set, otherwise raw value
    if (rowData.customer_name) {
      return rowData.customer_name;
    }
    if (rowData.raw_customer_name) {
      return <span className="raw-value">{rowData.raw_customer_name}</span>;
    }
    return '—';
  };

  const dateBodyTemplate = (rowData: CustomerDeposit) => {
    // Show final value if set, otherwise raw value
    if (rowData.deposit_date) {
      return formatDate(rowData.deposit_date);
    }
    if (rowData.raw_deposit_date) {
      // Try to parse and format, otherwise show raw
      const formatted = formatDate(rowData.raw_deposit_date);
      if (formatted !== '-') {
        return <span className="raw-value">{formatted}</span>;
      }
      return <span className="raw-value">{rowData.raw_deposit_date}</span>;
    }
    return '—';
  };

  const sourceBodyTemplate = (rowData: CustomerDeposit) => {
    const dataSource = dataSources.find((ds) => ds.id === rowData.data_source_id);
    return dataSource?.original_filename || dataSource?.name || '—';
  };

  const statusBodyTemplate = (rowData: CustomerDeposit) => {
    const status = getDepositStatus(rowData);
    let statusIcon;
    if (isStatusApproved(status)) {
      statusIcon = <i className="ms-out ms-out-check-circle ms-success" />;
    } else if (isStatusRejected(status)) {
      statusIcon = <i className="ms-out ms-out-cancel ms-danger" />;
    } else {
      statusIcon = <i className="ms-out ms-out-error-circle ms-info" />;
    }

    return (
      <span>
        {statusIcon}
        {rowData.is_potential_duplicate && (
          <Tag value="dupe" severity="warning" />
        )}
      </span>
    );
  };

  const actionBodyTemplate = (rowData: EditedCustomerDeposit) => {
    const status = getDepositStatus(rowData);

    if (isStatusApproved(status)) {
      return (
        <div className="row-actions">
          <Button
            icon="ms-out ms-out-lock"
            link
            tooltip="Unlock for edit"
            onClick={() => handleUnlock(rowData)}
          />
        </div>
      );
    }

    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-check"
          link
          severity="success"
          tooltip="Approve"
          onClick={() => handleApprove(rowData)}
        />
        {!isStatusRejected(status) && (
          <Button
            icon="ms-out ms-out-close"
            link
            severity="danger"
            tooltip="Reject"
            onClick={() => handleReject(rowData)}
          />
        )}
        {rowData.edited && (
          <Button
            icon="ms-out ms-out-undo"
            link
            tooltip="Undo changes"
            severity="info"
            onClick={() => handleUndo(rowData.id)}
          />
        )}
      </div>
    );
  };

  // Row expansion template for showing duplicates of an original
  const rowExpansionTemplate = (originalDeposit: CustomerDeposit) => {
    const duplicates = duplicateGroups.duplicatesByOriginalId.get(originalDeposit.id) || [];

    return (
      <div>
        <DataTable 
          value={duplicates} 
          dataKey="id" 
          size="small" 
          className="no-headers warnlight"
        >
          <Column
            body={statusBodyTemplate}
            style={{ width: '6%' }}
          />
          <Column
            field="deposit_date"
            header="Date"
            style={{ width: '12%' }}
            body={dateBodyTemplate}
            sortable={!includeDuplicates}
          />
          <Column 
            field="customer_name" 
            header="Customer" 
            body={customerNameBodyTemplate} 
            sortable={!includeDuplicates} 
            style={{ width: '25%' }} 
            filter={!includeDuplicates} 
            filterPlaceholder="Search customer" 
            showFilterMatchModes={false} 
          />
          <Column 
            header="Amount" 
            body={amountBodyTemplate} 
            sortable={!includeDuplicates} 
            field="amount" 
            style={{ width: '10%' }} 
          />
          <Column
            field="data_source_id"
            header="Source"
            body={sourceBodyTemplate}
            sortable={!includeDuplicates}
            style={{ width: '30%' }}
          />
          <Column
            body={(rowData: CustomerDeposit) => (
              <Button
                icon="ms-out ms-out-check"
                link
                severity="success"
                tooltip="Approve"
                onClick={() => handleApprove(rowData)}
              />
            )}
          />
        </DataTable>
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Menubar
        className="actions-menubar"
        model={menuItems}
        end={
          totalDuplicateCount > 0 ? (
            <div className="menu-item-actions-group warning">
              <label><i className="ms-out ms-out-warning" /> dupes ({totalDuplicateCount}): </label>
              <Button
                icon="ms-out ms-out-preview"
                label="review"
                severity="warning"
                size="small"
                tooltip="review duplicates"
                tooltipOptions={{ position: 'bottom' }}
                onClick={handleShowDuplicates}
              />
              {deletableDuplicates.length > 0 && (
                <Button
                  label="delete"
                  severity="danger"
                  icon="ms-out ms-out-delete"
                  size="small"
                  tooltip="delete duplicates"
                  tooltipOptions={{ position: 'bottom' }}
                  onClick={handleDeleteVisibleDuplicates}
                />
              )}
            </div>
          ) : undefined
        }
      />

      <DataTable
        ref={tableRef}
        value={filteredDeposits}
        dataKey="id"
        loading={isLoading}
        selectionMode="single"
        selection={selectedDeposit}
        onSelectionChange={handleSelectionChange}
        paginator
        first={first}
        rows={rows}
        onPage={onPage}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> {includeDuplicates ? 'originals' : 'deposits'}
              {totalAmount > 0 && <> · Total: <b>{formatCurrency(totalAmount)}</b></>}
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        filterDisplay="menu"
        filters={filters}
        onFilter={handleFilter}
        totalRecords={filteredRowCount}
        emptyMessage={includeDuplicates ? 'No duplicates found' : 'No deposits found'}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
        expandedRows={includeDuplicates ? expandedRows : undefined}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={includeDuplicates ? rowExpansionTemplate : undefined}
        editMode="cell"
        tableStyle={{ tableLayout: 'fixed' }}
      >
        {includeDuplicates && (
          <Column expander style={{ width: '3%' }} />
        )}
        <Column
          body={statusBodyTemplate}
          style={{ width: '3%' }}
        />
        <Column
          field="deposit_date"
          header="Date"
          style={{ width: '12%' }}
          body={dateBodyTemplate}
          sortable={!includeDuplicates}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          field="customer_name"
          header="Customer"
          body={customerNameBodyTemplate}
          sortable={!includeDuplicates}
          style={{ width: '25%' }}
          filter={!includeDuplicates}
          filterPlaceholder="Search customer"
          showFilterMatchModes={false}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          field="amount"
          header="Amount"
          body={amountBodyTemplate}
          sortable={!includeDuplicates}
          style={{ width: '15%' }}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column 
          field="data_source_id" 
          header="Source" 
          body={sourceBodyTemplate} 
          sortable={!includeDuplicates} 
          style={{ width: '30%' }} 
        />
        <Column body={actionBodyTemplate}  />
      </DataTable>
    </>
  );
};
